import React, { useMemo } from "react";

import { useQuery } from "@apollo/client";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { formatISO } from "date-fns/esm";
import { observer } from "mobx-react-lite";
import { Virtuoso } from "react-virtuoso";
import { makeStyles, withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import useDialogState from "@/hooks/useDialogState";
import NewClientDialog from "@/pages/Clients/ClientList/components/NewClientDialog";
import { colors } from "@/theme/overrides/colors";

import ClientItem from "./ClientItem";
import pinnedClients from "./store/PinnedClients";
import {
  sortByClientNameAsc,
  sortByClientNameDesc,
  sortByPinnedPrimarily,
  sortByStatus,
} from "./utils/sort";
import { useSidebarContext } from "../../Sidebar";
import HoverBase from "../ClickBase";

const useStyles = makeStyles()((theme: Theme) => ({
  users: {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
  },

  user: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(5),
  },

  link: {
    textDecoration: "none",
    color: theme.palette.common.black,
  },

  avatar: {
    width: "40px",
    height: "40px",
  },
}));

const StyledButtonBase = withStyles(ButtonBase, (theme: Theme) => ({
  root: {
    width: "100%",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: theme.spacing(2, 2),
    "&:hover": {
      backgroundColor: "#EDF0F5",
      cursor: "pointer",
    },
  },
}));

const clientListQuery = graphql(`
  query ClientList($currentDate: ISO8601Date!) {
    clients {
      nodes {
        id
        profile {
          id
          fullName
          avatarUrl(variant: THUMB)
        }
        programForDate(date: $currentDate) {
          id
          name
        }
        status
      }
    }
  }
`);

const ClientList: React.FC = observer(() => {
  const { classes } = useStyles();
  const { dispatch } = useSidebarContext();
  const currentDate = formatISO(new Date(), { representation: "date" });
  const { data, refetch } = useQuery(clientListQuery, {
    variables: { currentDate: currentDate },
  });
  const clients = data?.clients.nodes ?? [];
  const [sortValue, setSortValue] =
    React.useState<"status" | "asc" | "desc">("status");
  const [searchText, setSearchText] = React.useState("");
  const addClientDialog = useDialogState();

  function handleSort(e: SelectChangeEvent) {
    const sort = e.target.value as "status" | "asc" | "desc";
    setSortValue(sort);
  }

  function handleSearch(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchText(event.target.value);
  }

  const allClients = useMemo(() => {
    const secondarySort = {
      status: sortByStatus,
      asc: sortByClientNameAsc,
      desc: sortByClientNameDesc,
    }[sortValue];
    return [...clients]
      .filter((client) => {
        const search = searchText.toLowerCase();
        return client.profile?.fullName.toLowerCase().includes(search);
      })
      .sort(sortByPinnedPrimarily(secondarySort));
  }, [clients, sortValue, searchText, pinnedClients.size]);

  return (
    <>
      <HoverBase componentName="clients" expandLink="/clients">
        <div>
          <Box
            paddingX={2}
            flexGrow={1}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            position="relative"
          >
            <Button
              onClick={() => {
                dispatch({ type: "close-component" });
                addClientDialog.show();
              }}
            >
              Add new client
            </Button>
            <FormControl variant="outlined" size="small">
              <Select
                value={sortValue}
                inputProps={{ "aria-label": "Without label" }}
                onChange={handleSort}
              >
                <MenuItem value={""} disabled>
                  Sort by
                </MenuItem>
                <MenuItem value="status">Status</MenuItem>
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box
            paddingX={2}
            marginY={2}
            flexGrow={1}
            display="flex"
            alignItems="center"
          >
            <TextField
              value={searchText}
              onChange={handleSearch}
              fullWidth
              size="small"
              placeholder="Search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon htmlColor={colors.grey} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </div>

        <div className={classes.users}>
          {allClients?.length === 0 ? (
            <div className={classes.user}>
              <Typography>No user found</Typography>
            </div>
          ) : (
            <>
              <Virtuoso
                data={allClients}
                itemContent={(index, client) => (
                  <>
                    {!pinnedClients.has(client.id) &&
                      pinnedClients.has(
                        allClients[Math.max(index - 1, 0)].id
                      ) && <Divider sx={{ mx: 2 }} />}
                    <ClientItem
                      client={client}
                      onPin={(id) => pinnedClients.pin(id)}
                      onUnpin={(id) => pinnedClients.unpin(id)}
                    />
                  </>
                )}
              />
            </>
          )}
        </div>
      </HoverBase>

      <NewClientDialog
        {...addClientDialog.props}
        afterCreate={async () => {
          await refetch();
          addClientDialog.hide();
        }}
      />
    </>
  );
});

export default ClientList;
